import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  ScrollView,
  TouchableOpacity,
  Alert, 
  ActivityIndicator,
  FlatList
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  doc, 
  getDoc, 
  Timestamp
} from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainTabParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import { useTheme } from '../../context/ThemeContext';

type Props = NativeStackScreenProps<MainTabParamList, 'Progress'>;

interface WorkoutHistory {
  id: string;
  routineId: string;
  routineName: string;
  dayOrder: number;
  dayName: string;
  date: Timestamp;
  duration: number; // in minutes
  exercisesCompleted: number;
  notes?: string;
}

interface ExerciseProgress {
  id: string;
  exerciseId: string;
  exerciseName: string;
  routineId: string;
  routineName: string;
  weight: number;
  date: Timestamp;
}

const ProgressHistoryScreen = ({ navigation }: Props) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [workoutHistory, setWorkoutHistory] = useState<WorkoutHistory[]>([]);
  const [recentProgress, setRecentProgress] = useState<ExerciseProgress[]>([]);
  const [activeTab, setActiveTab] = useState<'workouts' | 'exercises'>('workouts');

  useEffect(() => {
    fetchUserProgress();
  }, []);

  const fetchUserProgress = async () => {
    try {
      setLoading(true);
      
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Fetch workout history
      await fetchWorkoutHistory(userId);
      
      // Fetch recent progress
      await fetchRecentProgress(userId);
    } catch (error) {
      console.error('Error al obtener historial de progreso:', error);
      Alert.alert('Error', 'No se pudo cargar el historial de progreso');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkoutHistory = async (userId: string) => {
    const historyRef = collection(firestore, 'workoutHistory');
    const q = query(
      historyRef,
      where('userId', '==', userId),
      orderBy('date', 'desc'),
      limit(20)
    );
    
    const querySnapshot = await getDocs(q);
    const history: WorkoutHistory[] = [];
    
    // Get routine IDs to fetch routine names
    const routineIds = new Set<string>();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      routineIds.add(data.routineId);
      
      history.push({
        id: doc.id,
        routineId: data.routineId,
        routineName: 'Cargando...',
        dayOrder: data.dayOrder,
        dayName: data.dayName || `Día ${data.dayOrder}`,
        date: data.date,
        duration: data.duration || 0,
        exercisesCompleted: data.exercisesCompleted || 0,
        notes: data.notes
      });
    });
    
    // Get routine names
    if (routineIds.size > 0) {
      const routineNames: {[key: string]: string} = {};
      
      await Promise.all(
        Array.from(routineIds).map(async (routineId) => {
          try {
            const routineDoc = await getDoc(doc(firestore, 'routines', routineId));
            if (routineDoc.exists()) {
              routineNames[routineId] = routineDoc.data().name;
            }
          } catch (error) {
            console.error(`Error al obtener rutina ${routineId}:`, error);
          }
        })
      );
      
      // Update routine names
      history.forEach(item => {
        item.routineName = routineNames[item.routineId] || 'Rutina desconocida';
      });
    }
    
    setWorkoutHistory(history);
  };

  const fetchRecentProgress = async (userId: string) => {
    const progressRef = collection(firestore, 'progress');
    const q = query(
      progressRef,
      where('userId', '==', userId),
      orderBy('date', 'desc'),
      limit(20)
    );
    
    const querySnapshot = await getDocs(q);
    const progressItems: ExerciseProgress[] = [];
    
    // Get routine and exercise IDs
    const routineIds = new Set<string>();
    const exerciseIds = new Set<string>();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      routineIds.add(data.routineId);
      exerciseIds.add(data.exerciseId);
      
      progressItems.push({
        id: doc.id,
        exerciseId: data.exerciseId,
        exerciseName: 'Cargando...',
        routineId: data.routineId,
        routineName: 'Cargando...',
        weight: data.weight,
        date: data.date
      });
    });
    
    // Get routine names
    const routineNames: {[key: string]: string} = {};
    const exerciseNames: {[key: string]: string} = {};
    
    await Promise.all([
      // Fetch routine names
      ...Array.from(routineIds).map(async (routineId) => {
        try {
          const routineDoc = await getDoc(doc(firestore, 'routines', routineId));
          if (routineDoc.exists()) {
            routineNames[routineId] = routineDoc.data().name;
          }
        } catch (error) {
          console.error(`Error al obtener rutina ${routineId}:`, error);
        }
      }),
      
      // Fetch exercise names
      ...Array.from(exerciseIds).map(async (exerciseId) => {
        try {
          const exerciseDoc = await getDoc(doc(firestore, 'exercises', exerciseId));
          if (exerciseDoc.exists()) {
            exerciseNames[exerciseId] = exerciseDoc.data().name;
          }
        } catch (error) {
          console.error(`Error al obtener ejercicio ${exerciseId}:`, error);
        }
      })
    ]);
    
    // Update names
    progressItems.forEach(item => {
      item.routineName = routineNames[item.routineId] || 'Rutina desconocida';
      item.exerciseName = exerciseNames[item.exerciseId] || 'Ejercicio desconocido';
    });
    
    setRecentProgress(progressItems);
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    
    return `${mins}m`;
  };

  const renderWorkoutItem = ({ item }: { item: WorkoutHistory }) => (
    <Card style={styles.historyItem}>
      <View style={styles.historyHeader}>
        <Text style={[styles.historyDate, { color: theme.colors.text }]}>{formatDate(item.date)}</Text>
        <Text style={[styles.routineName, { color: theme.colors.grey.dark }]}>{item.routineName}</Text>
      </View>
      
      <View style={styles.historyContent}>
        <View style={styles.historyDetail}>
          <Text style={[styles.detailValue, { color: theme.colors.primary }]}>{item.dayName}</Text>
          <Text style={[styles.detailLabel, { color: theme.colors.grey.dark }]}>Día</Text>
        </View>
        
        <View style={styles.historyDetail}>
          <Text style={[styles.detailValue, { color: theme.colors.primary }]}>{formatTime(item.duration)}</Text>
          <Text style={[styles.detailLabel, { color: theme.colors.grey.dark }]}>Duración</Text>
        </View>
        
        <View style={styles.historyDetail}>
          <Text style={[styles.detailValue, { color: theme.colors.primary }]}>{item.exercisesCompleted}</Text>
          <Text style={[styles.detailLabel, { color: theme.colors.grey.dark }]}>Ejercicios</Text>
        </View>
      </View>
      
      {item.notes && (
        <View style={[styles.notesContainer, { borderTopColor: theme.colors.grey.light }]}>
          <Text style={[styles.notesLabel, { color: theme.colors.grey.dark }]}>Notas:</Text>
          <Text style={[styles.notesText, { color: theme.colors.text }]}>{item.notes}</Text>
        </View>
      )}
    </Card>
  );

  const renderProgressItem = ({ item }: { item: ExerciseProgress }) => (
    <TouchableOpacity
      style={[styles.progressItem, { backgroundColor: theme.colors.card }, theme.shadows.light]}
      onPress={() => navigation.navigate('WeightTracking', { exerciseId: item.exerciseId })}
    >
      <View style={styles.progressHeader}>
        <Text style={[styles.exerciseName, { color: theme.colors.text }]}>{item.exerciseName}</Text>
        <Text style={[styles.progressDate, { color: theme.colors.grey.dark }]}>{formatDate(item.date)}</Text>
      </View>
      
      <View style={styles.progressContent}>
        <View style={styles.progressWeight}>
          <Text style={[styles.weightValue, { color: theme.colors.primary }]}>{item.weight}</Text>
          <Text style={[styles.weightUnit, { color: theme.colors.primary }]}>kg</Text>
        </View>
        
        <Text style={[styles.routineName, { color: theme.colors.grey.dark }]}>{item.routineName}</Text>
      </View>
      
      <View style={styles.viewDetailsContainer}>
        <Text style={[styles.viewDetailsText, { color: theme.colors.primary }]}>Ver Detalles</Text>
        <Ionicons name="chevron-forward" size={16} color={theme.colors.primary} />
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.tabsContainer, { backgroundColor: theme.colors.card }, theme.shadows.light]}>
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'workouts' && [styles.activeTab, { borderBottomColor: theme.colors.primary }]
          ]}
          onPress={() => setActiveTab('workouts')}
        >
          <Text
            style={[
              styles.tabText,
              { color: theme.colors.grey.dark },
              activeTab === 'workouts' && { color: theme.colors.primary }
            ]}
          >
            Entrenamientos
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[
            styles.tab,
            activeTab === 'exercises' && [styles.activeTab, { borderBottomColor: theme.colors.primary }]
          ]}
          onPress={() => setActiveTab('exercises')}
        >
          <Text
            style={[
              styles.tabText,
              { color: theme.colors.grey.dark },
              activeTab === 'exercises' && { color: theme.colors.primary }
            ]}
          >
            Ejercicios
          </Text>
        </TouchableOpacity>
      </View>
      
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : activeTab === 'workouts' ? (
        <View style={styles.historyContainer}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Historial de Entrenamientos</Text>
          
          {workoutHistory.length > 0 ? (
            <FlatList
              data={workoutHistory}
              renderItem={renderWorkoutItem}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.historyList}
            />
          ) : (
            <View style={styles.emptyContainer}>
              <Ionicons name="fitness-outline" size={64} color={theme.colors.grey.medium} />
              <Text style={[styles.emptyText, { color: theme.colors.text }]}>No hay historial de entrenamientos</Text>
              <Text style={[styles.emptySubtext, { color: theme.colors.grey.dark }]}>
                Completa entrenamientos para ver tu progreso aquí
              </Text>
            </View>
          )}
        </View>
      ) : (
        <View style={styles.progressContainer}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Progreso de Ejercicios</Text>
          
          {recentProgress.length > 0 ? (
            <FlatList
              data={recentProgress}
              renderItem={renderProgressItem}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.progressList}
            />
          ) : (
            <View style={styles.emptyContainer}>
              <Ionicons name="barbell-outline" size={64} color={theme.colors.grey.medium} />
              <Text style={[styles.emptyText, { color: theme.colors.text }]}>No hay registros de progreso</Text>
              <Text style={[styles.emptySubtext, { color: theme.colors.grey.dark }]}>
                Registra tu progreso en los ejercicios para ver tu evolución aquí
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabsContainer: {
    flexDirection: 'row',
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
  },
  tabText: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  activeTabText: {
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.text,
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    marginTop: spacing.lg,
  },
  historyContainer: {
    flex: 1,
  },
  historyList: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
  },
  historyItem: {
    marginBottom: spacing.md,
  },
  historyHeader: {
    marginBottom: spacing.md,
  },
  historyDate: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    color: colors.text,
  },
  routineName: {
    fontSize: typography.fontSize.sm,
    color: colors.grey.dark,
  },
  historyContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  historyDetail: {
    alignItems: 'center',
  },
  detailValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.primary,
  },
  detailLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.grey.dark,
    marginTop: 2,
  },
  notesContainer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey.light,
    paddingTop: spacing.sm,
  },
  notesLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    color: colors.grey.dark,
    marginBottom: spacing.xs,
  },
  notesText: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  progressContainer: {
    flex: 1,
  },
  progressList: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
  },
  progressItem: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  exerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    color: colors.text,
    flex: 1,
  },
  progressDate: {
    fontSize: typography.fontSize.sm,
    color: colors.grey.dark,
  },
  progressContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  progressWeight: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  weightValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    color: colors.primary,
  },
  weightUnit: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    marginLeft: 2,
  },
  viewDetailsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  viewDetailsText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    marginRight: spacing.xs,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  emptySubtext: {
    fontSize: typography.fontSize.md,
    color: colors.grey.dark,
    textAlign: 'center',
  },
});

export default ProgressHistoryScreen; 